import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { FormBuilder, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ToolbarComponent } from "../../../shared/components/toolbar/toolbar.component";
import { Observable, Subject, combineLatest, map, shareReplay, startWith, takeUntil, tap } from "rxjs";
import { RouterModule } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { BillingService } from "../../services/billing.service";
import { getPaymentProductName, IPayment } from "../../model/payment.model";
import { BillingThumbComponent } from "../billing-thumb/billing-thumb.component";
import { TranslocoCurrencyPipe } from "@jsverse/transloco-locale";
import { CalendarModule } from "primeng/calendar";
import { PrimeNGConfig } from "primeng/api";

@Component({
    selector: 'clp-revenue-list',
    styleUrls: ['./revenue-List.component.scss'],
    templateUrl: './revenue-List.component.html',
    imports: [
        RouterModule,
        TranslateModule,
        CommonModule,
        ReactiveFormsModule,
        ToolbarComponent,
        BillingThumbComponent,
        TranslocoCurrencyPipe,
        CalendarModule
    ],
    providers: [
        BillingService,
        ToastrService
    ]
})

export class RevenueListComponent implements OnInit, OnDestroy {
    private fb = inject(FormBuilder);
    private translate = inject(TranslateService);
    private config = inject(PrimeNGConfig);
    private billingService = inject(BillingService);
    private payments$!: Observable<IPayment[]>;
    public filteredPayments$!: Observable<IPayment[]>;
    private destroy$ = new Subject<void>();
    total: number = 0;
    currency: string = 'USD';
    paidAmount: number = 0;
    paidCount: number = 0;
    unpaidCount: number = 0;
    averageTicket: number = 0;
    courseOptions: string[] = [];
    userOptions: string[] = [];
    readonly statusOptions: string[] = ['paid', 'unpaid', 'unknown'];
    readonly filterForm = this.fb.group({
        fromDate: null as Date | null,
        toDate: null as Date | null,
        course: '',
        user: '',
        status: ''
    });

    ngOnInit(): void {
        this.setPrimeNGConfig();
        this.translate.onLangChange
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.setPrimeNGConfig());

        this.payments$ = this.billingService.getPayments().pipe(
            tap((payments) => this.updateFilterOptions(payments)),
            shareReplay(1)
        );

        this.filteredPayments$ = combineLatest([
            this.payments$,
            this.filterForm.valueChanges.pipe(startWith(this.filterForm.getRawValue()))
        ]).pipe(
            map(([payments, filters]) => this.applyFilters(payments, filters)),
            tap((payments) => this.updateSummary(payments))
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    clearFilters(): void {
        this.filterForm.reset({
            fromDate: null,
            toDate: null,
            course: '',
            user: '',
            status: ''
        });
    }

    private updateSummary(payments: IPayment[]): void {
        this.total = payments.reduce((sum, payment) => sum + payment.amount, 0);
        this.paidCount = payments.filter((payment) => this.isPaidPayment(payment)).length;
        this.unpaidCount = Math.max(payments.length - this.paidCount, 0);
        this.paidAmount = payments
            .filter((payment) => this.isPaidPayment(payment))
            .reduce((sum, payment) => sum + payment.amount, 0);
        this.averageTicket = payments.length > 0 ? this.total / payments.length : 0;
        this.currency = payments.find((payment) => !!payment.currency)?.currency ?? this.currency;
    }

    private isPaidPayment(payment: IPayment): boolean {
        const status = (payment.paymentStatus ?? '').toLowerCase();
        return payment.paid || status === 'paid' || status === 'succeeded' || status === 'captured';
    }

    private updateFilterOptions(payments: IPayment[]): void {
        this.courseOptions = [...new Set(payments.map((payment) => getPaymentProductName(payment)).filter((name): name is string => !!name))].sort((a, b) => a.localeCompare(b));
        this.userOptions = [...new Set(payments.map((payment) => payment.userName).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    }

    private applyFilters(
        payments: IPayment[],
        filters: Partial<{ fromDate: Date | string | null; toDate: Date | string | null; course: string | null; user: string | null; status: string | null; }>
    ): IPayment[] {
        const fromDate = this.normalizeDate(filters.fromDate, true);
        const toDate = this.normalizeDate(filters.toDate, false);
        const selectedCourse = (filters.course ?? '').trim().toLowerCase();
        const selectedUser = (filters.user ?? '').trim().toLowerCase();
        const selectedStatus = (filters.status ?? '').trim().toLowerCase();

        return payments.filter((payment) => {
            const paymentDate = new Date(payment.createdAt);
            if (Number.isNaN(paymentDate.getTime())) {
                return false;
            }

            if (fromDate && paymentDate < fromDate) {
                return false;
            }

            if (toDate && paymentDate > toDate) {
                return false;
            }

            if (selectedCourse && getPaymentProductName(payment).toLowerCase() !== selectedCourse) {
                return false;
            }

            if (selectedUser && (payment.userName ?? '').toLowerCase() !== selectedUser) {
                return false;
            }

            if (selectedStatus && (payment.paymentStatus ?? '').toLowerCase() !== selectedStatus) {
                return false;
            }

            return true;
        });
    }

    private normalizeDate(value: Date | string | null | undefined, isStart: boolean): Date | null {
        if (!value) {
            return null;
        }

        const date = value instanceof Date ? new Date(value) : new Date(value);
        if (Number.isNaN(date.getTime())) {
            return null;
        }

        if (isStart) {
            date.setHours(0, 0, 0, 0);
        } else {
            date.setHours(23, 59, 59, 999);
        }

        return date;
    }

    private setPrimeNGConfig(): void {
        this.config.setTranslation({
            accept: this.translate.instant("general.calendar.accept"),
            reject: this.translate.instant("general.calendar.reject"),
            dayNames: this.translate.instant("general.calendar.dayNames"),
            dayNamesShort: this.translate.instant("general.calendar.dayNamesShort"),
            dayNamesMin: this.translate.instant("general.calendar.dayNamesMin"),
            monthNames: this.translate.instant("general.calendar.monthNames"),
            monthNamesShort: this.translate.instant("general.calendar.monthNamesShort"),
            today: this.translate.instant("general.calendar.today"),
            clear: this.translate.instant("general.calendar.clear"),
            dateFormat: this.translate.instant("general.calendar.dateFormat"),
            weekHeader: this.translate.instant("general.calendar.weekHeader")
        });
    }
}
